import { Button } from "@/components/ui/baseUi/button"
import { CalendarInput } from "@/components/ui/forms/calendarInput"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { displayedFullDateFormat } from "@/modules/utils/dateUtils"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { ArrowLeft, CalendarDays } from "lucide-react"
import { type Dispatch, type SetStateAction } from "react"
import * as Yup from "yup"

export type MemberSendOfferModalStartDateProps = {
  deferredDate: Date | null
  endDate: Date | null
  setDeferredDate: Dispatch<SetStateAction<Date | null>>
  setIsDeferredDateVisible: Dispatch<SetStateAction<boolean>>
  className?: string
} & React.ComponentProps<"div">

const deferredDateYupValidation = (endDate: Date | null) =>
  Yup.object({
    defferedDate: Yup.date()
      .nullable()
      .min(dayjs().toDate(), word("member.send.offer.modal.startDate.errorForm"))
      .max(
        endDate ? dayjs(endDate).toDate() : dayjs().add(20, "year").toDate(),
        word("member.send.offer.modal.startDate.errorFormBiggerThanEndDate"),
      ),
  })

export function MemberSendOfferModalStartDate({
  deferredDate,
  endDate,
  setDeferredDate,
  setIsDeferredDateVisible,
  className,
  ...props
}: MemberSendOfferModalStartDateProps) {
  const formik = useFormik<{ defferedDate: Date | null }>({
    initialValues: { defferedDate: deferredDate },
    validationSchema: deferredDateYupValidation(endDate),
    onSubmit: () => {
      try {
        setDeferredDate(formik.values.defferedDate)
        setIsDeferredDateVisible(false)
      } catch (e: any) {
        console.error(LoggerType.UI + "defferred date in send box offer", e)
      }
    },
  })

  return (
    <div className={cn("mt-6 flex w-full grow flex-col gap-6", className)} {...props}>
      {/* TITLE */}
      <div className="flex items-center justify-center gap-2">
        <CalendarDays color="currentColor" strokeWidth={1} size={13} className="text-textDefault-body" />
        <Text fontStyle="TS" className="text-center text-textDefault-body">
          {word("member.send.offer.modal.startDate.title")}
        </Text>
      </div>

      <CalendarInput
        className="grow"
        label={word("member.send.offer.modal.startDate.titleInput")}
        name="defferedDate"
        getFieldProps={formik.getFieldProps}
        error={getFormErrorMessage("defferedDate", formik)}
        setFieldValue={formik.setFieldValue}
        placeholder={dayjs().format("L")}
        calendarProps={{
          minDate: dayjs().startOf("day").toDate(),
          maxDate: dayjs().add(1, "year").toDate(),
          dateFormat: displayedFullDateFormat,
        }}
      />

      {/* BUTTON */}
      <div className="mt-2 flex w-full flex-row justify-center gap-2">
        <Button
          variant="outline"
          size="lg"
          className="grow"
          onClick={() => {
            setIsDeferredDateVisible(false)
            setDeferredDate(null)
          }}
        >
          <ArrowLeft strokeWidth={2} color="currentColor" size={15} />
          {deferredDate !== null ? word("global.reset") : word("global.cancel")}
        </Button>
        <Button variant="primary" size="lg" className="grow" onClick={() => formik.handleSubmit()}>
          {word("global.check")}
        </Button>
      </div>
    </div>
  )
}
